/**
 * useInterpreter v0.3
 *
 * 通訳モードのコア。Web Speech API + Claude API を結びつける：
 *   1. start() でマイク許可を取り、SpeechRecognition を継続認識で起動
 *   2. interim 結果は state に流し続ける（ORIGINAL ブロックの「LIVE」表示用）
 *   3. isFinal セグメントを翻訳キューに push、3語未満/5文字未満は無視
 *   4. キューを順番に translateSpeech へ送信、確定和訳を state に
 *   5. stop() で recognition を停止し idle へ
 *
 * 状態：
 *   InterpretStatus = 'idle' | 'listening' | 'translating' | 'error'
 *
 * エラーは以下を区別:
 *   - not-allowed:    マイク不許可
 *   - no-speech:      長時間の無音
 *   - network:        SR ネットワークエラー
 *   - audio-capture:  マイクハードウェア問題
 *   - api:            Claude API（429 含む）
 *
 * 連続3回エラー or 429 で自動停止＋停止理由を errorMessage に付加。
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { ClaudeApiError, translateSpeech } from '../lib/claude';

import type { InterpretStatus, ScanResult } from '../types';

/** 翻訳キューに乗せる最低条件（要件: 3語以上 かつ 5文字以上） */
const MIN_WORDS = 3;
const MIN_CHARS = 5;

/** 翻訳1回あたりのタイムアウト（10秒） */
const TRANSLATE_TIMEOUT_MS = 10_000;

/** 連続エラー上限 */
const MAX_CONSECUTIVE_ERRORS = 3;

/** 認識言語（v0.3 は EN→JA 固定） */
const RECOGNITION_LANG = 'en-US';

/**
 * v0.3.1: isFinalセグメントのバッファリング設定
 * - FLUSH_DELAY_MS: 確定セグメント受信後この時間次が来なければ「話し終わり」とみなして flush
 * - MAX_BUFFER_CHARS: バッファが大きくなりすぎたら即時 flush（応答遅延の上限）
 */
const FLUSH_DELAY_MS = 1500;
const MAX_BUFFER_CHARS = 200;

export interface UseInterpreterArgs {
  /** APIキー */
  apiKey: string | null;
  /** 1セグメント確定→翻訳が完了したら呼ばれる（履歴保存用） */
  onSegmentTranslated?: (result: ScanResult) => void;
}

export interface UseInterpreterResult {
  status: InterpretStatus;
  /** 直近の確定原文（翻訳が完了したセグメント） */
  finalOriginal: string;
  /** 認識中の暫定テキスト（リアルタイム、未確定） */
  interimOriginal: string;
  /**
   * v0.3.1: バッファリング中の確定済みセグメント。
   * 確定はしているが、まだ翻訳キューに乗っていない（FLUSH_DELAY_MS待機中、
   * または MAX_BUFFER_CHARS未満）テキスト。UI 上は finalOriginal の後ろに
   * 「これから翻訳される予定」として表示する想定。
   */
  pendingOriginal: string;
  /** バッファに何か入っている（pendingOriginal が空でない）か */
  isBuffering: boolean;
  /** 直近の確定和訳 */
  japanese: string;
  /** ユーザー向けエラーメッセージ */
  errorMessage: string | null;
  /** 開始（マイク許可ダイアログを発火させるためユーザー操作起点で呼ぶこと） */
  start: () => Promise<void>;
  /** 停止（バッファを最後に flush してから recognition を止める） */
  stop: () => void;
  /** リセット（テキストとエラーをクリアして idle へ） */
  reset: () => void;
}

/**
 * SR エラー名 → ユーザー向け日本語メッセージ
 */
function srErrorToMessage(errName: string): string {
  switch (errName) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'マイクの使用が許可されていません。ブラウザ設定からマイクを許可してください。';
    case 'no-speech':
      return '音声を検出できませんでした。マイクに向かって話してください。';
    case 'audio-capture':
      return 'マイクが利用できません。他のアプリがマイクを使用していないか確認してください。';
    case 'network':
      return '音声認識サーバーに接続できませんでした。通信状態を確認してください。';
    case 'language-not-supported':
      return 'お使いのブラウザは英語の音声認識に対応していません。';
    default:
      return `音声認識エラー（${errName}）。`;
  }
}

/** ScanResult の id 生成 */
function makeId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** 翻訳に値するセグメントか（短すぎ・短語数を弾く） */
function isTranslatableSegment(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length < MIN_CHARS) return false;
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length < MIN_WORDS) return false;
  return true;
}

/** ブラウザの SpeechRecognition コンストラクタを取得（無ければ null） */
function getRecognitionCtor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

export function useInterpreter(args: UseInterpreterArgs): UseInterpreterResult {
  const { apiKey, onSegmentTranslated } = args;

  const [status, setStatus] = useState<InterpretStatus>('idle');
  const [finalOriginal, setFinalOriginal] = useState('');
  const [interimOriginal, setInterimOriginal] = useState('');
  const [pendingOriginal, setPendingOriginal] = useState('');
  const [japanese, setJapanese] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // SR インスタンスは ref で保持（再レンダー越しにも維持）
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  // 翻訳キューと進行中フラグ（順番保証）
  const queueRef = useRef<string[]>([]);
  const drainingRef = useRef(false);

  // v0.3.1: isFinalセグメントを結合するバッファ
  const pendingSegmentRef = useRef<string>('');
  const flushTimerRef = useRef<number | null>(null);

  // 連続エラーカウンタ
  const consecutiveErrorsRef = useRef(0);

  // 最新コールバックを ref で参照（依存に入れずに済ませる）
  const onSegmentTranslatedRef = useRef(onSegmentTranslated);
  useEffect(() => {
    onSegmentTranslatedRef.current = onSegmentTranslated;
  }, [onSegmentTranslated]);

  const apiKeyRef = useRef(apiKey);
  useEffect(() => {
    apiKeyRef.current = apiKey;
  }, [apiKey]);

  /**
   * 自動停止（429 or 連続エラー上限）
   * バッファや進行中ドレインも全部破棄してエラー表示で止める
   */
  const haltWithReason = useCallback((baseMessage: string, suffix: string) => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // 既に止まっている場合の例外は無視
      }
    }
    if (flushTimerRef.current) {
      window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    queueRef.current = [];
    drainingRef.current = false;
    pendingSegmentRef.current = '';
    setPendingOriginal('');
    setStatus('error');
    setErrorMessage(`${baseMessage}\n${suffix}`);
  }, []);

  /**
   * キューを順番にドレイン：1セグメントずつ翻訳完了を待ってから次へ
   */
  const drain = useCallback(async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    try {
      while (queueRef.current.length > 0) {
        const seg = queueRef.current.shift();
        if (!seg) break;

        const currentApiKey = apiKeyRef.current;
        if (!currentApiKey) {
          haltWithReason(
            'APIキーが設定されていません。',
            '（通訳セッションを停止しました）',
          );
          return;
        }

        setStatus('translating');

        // タイムアウト用 AbortController
        const ctrl = new AbortController();
        const timer = window.setTimeout(() => ctrl.abort(), TRANSLATE_TIMEOUT_MS);

        try {
          const ja = await translateSpeech({
            apiKey: currentApiKey,
            text: seg,
            signal: ctrl.signal,
          });
          window.clearTimeout(timer);

          // 結果反映
          setFinalOriginal(seg);
          setJapanese(ja);
          consecutiveErrorsRef.current = 0;

          // 履歴用 ScanResult を親に渡す
          const result: ScanResult = {
            id: makeId(),
            mode: 'interpret',
            text: ja,
            timestamp: new Date().toISOString(),
            original: seg,
            japanese: ja,
            sourceLang: 'en',
          };
          onSegmentTranslatedRef.current?.(result);
        } catch (err) {
          window.clearTimeout(timer);
          consecutiveErrorsRef.current += 1;

          const baseMessage =
            err instanceof ClaudeApiError
              ? err.userMessage
              : err instanceof Error
                ? err.message
                : '翻訳に失敗しました。';

          const isRateLimit = err instanceof ClaudeApiError && err.status === 429;
          const isMaxErrors = consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS;

          if (isRateLimit) {
            haltWithReason(
              baseMessage,
              `（レート制限のため通訳セッションを自動停止しました）`,
            );
            return;
          }
          if (isMaxErrors) {
            haltWithReason(
              baseMessage,
              `（${MAX_CONSECUTIVE_ERRORS}回連続でエラーが発生したため通訳セッションを自動停止しました）`,
            );
            return;
          }
          // 上限未満：エラー表示してもセッションは継続
          setErrorMessage(baseMessage);
        }
      }
      // キューを処理し終えたら：
      //   - recognition が継続中なら listening に戻す
      //   - 既に stop 済み（recognitionRef=null）なら idle へ
      if (recognitionRef.current) {
        setStatus('listening');
      } else {
        setStatus('idle');
      }
    } finally {
      drainingRef.current = false;
    }
  }, [haltWithReason]);

  /**
   * v0.3.1: バッファに溜まった確定セグメントを翻訳キューに移して drain を起動
   *
   * - pendingSegmentRef を空にし、UI 表示も同期クリア
   * - 待機中のタイマーがあれば cancel
   * - queueRef.push 後、drain は非同期で進行
   */
  const flushPending = useCallback(() => {
    const text = pendingSegmentRef.current;
    if (!text) return;

    pendingSegmentRef.current = '';
    setPendingOriginal('');

    if (flushTimerRef.current) {
      window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }

    queueRef.current.push(text);
    void drain();
  }, [drain]);

  /**
   * v0.3.1: isFinal セグメントをバッファに連結する
   *
   * - 3語以上＆5文字以上のフィルタを通過したものだけ採用
   * - バッファが既存にあれば半角スペースで連結
   * - MAX_BUFFER_CHARS 超えたら即時 flush
   * - 上限未満なら FLUSH_DELAY_MS のタイマーを張り直す
   */
  const enqueueFinal = useCallback(
    (rawText: string) => {
      if (!isTranslatableSegment(rawText)) return;
      const trimmed = rawText.trim();

      const next = pendingSegmentRef.current
        ? `${pendingSegmentRef.current} ${trimmed}`
        : trimmed;
      pendingSegmentRef.current = next;
      setPendingOriginal(next);

      if (next.length >= MAX_BUFFER_CHARS) {
        flushPending();
        return;
      }

      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current);
      }
      flushTimerRef.current = window.setTimeout(() => {
        flushTimerRef.current = null;
        flushPending();
      }, FLUSH_DELAY_MS);
    },
    [flushPending],
  );

  /**
   * SpeechRecognition の onresult ハンドラ
   * - 確定セグメントは enqueueFinal でバッファに連結（v0.3.1+）
   * - 未確定の interim は state へ
   */
  const handleResult = useCallback(
    (event: SpeechRecognitionEvent) => {
      let interim = '';
      const newFinals: string[] = [];

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const r = event.results[i];
        if (!r) continue;
        const alt = r[0];
        if (!alt) continue;
        if (r.isFinal) {
          newFinals.push(alt.transcript);  // フィルタは enqueueFinal で
        } else {
          interim += alt.transcript;
        }
      }

      setInterimOriginal(interim.trim());

      for (const seg of newFinals) {
        enqueueFinal(seg);
      }
    },
    [enqueueFinal],
  );

  /**
   * SpeechRecognition の onerror ハンドラ
   */
  const handleError = useCallback((event: SpeechRecognitionErrorEvent) => {
    const errName = event.error;
    console.error('[useInterpreter] SR error:', errName, event.message);

    // no-speech は無視（連続認識中の長い無音は致命的でない）
    if (errName === 'no-speech') {
      return;
    }
    // aborted は明示的な stop の結果なので何もしない
    if (errName === 'aborted') {
      return;
    }

    consecutiveErrorsRef.current += 1;
    const message = srErrorToMessage(errName);

    if (errName === 'not-allowed' || errName === 'service-not-allowed') {
      // マイク不許可は即停止
      haltWithReason(message, '（マイク許可がないためセッションを停止しました）');
      return;
    }

    if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
      haltWithReason(
        message,
        `（${MAX_CONSECUTIVE_ERRORS}回連続でエラーが発生したため通訳セッションを自動停止しました）`,
      );
      return;
    }

    setErrorMessage(message);
  }, [haltWithReason]);

  /**
   * SpeechRecognition の onend ハンドラ
   * 連続認識中に Safari等で勝手に切れた場合は再開する。意図的な stop 後は idle 維持。
   */
  const handleEnd = useCallback(() => {
    // status を関数引数で読まずrefでも取れるが、シンプルに state へ問い合わせるために
    // 再start 判定は recognitionRef の存在で行う：
    //   stop() で null になっているはず
    if (!recognitionRef.current) return;
    try {
      recognitionRef.current.start();
    } catch (err) {
      console.warn('[useInterpreter] auto-restart failed:', err);
    }
  }, []);

  /**
   * 停止：
   *   - v0.3.1+: バッファに残っているセグメントを最後に翻訳キューへ送る
   *     （言いかけのセンテンスを救済）
   *   - recognition.stop で SR を停止（SR 由来の音声は終わる）
   *   - 既存キューのドレインは完走させる（最後の翻訳が見える）
   *   - drain 完了時に recognitionRef=null を見て idle に遷移
   */
  const stop = useCallback(() => {
    // バッファ flush（pending をキューに移して drain 起動）
    if (pendingSegmentRef.current) {
      flushPending();
    } else if (flushTimerRef.current) {
      // pending空＋タイマー残（理論上ない）の保険でクリア
      window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }

    const rec = recognitionRef.current;
    recognitionRef.current = null;  // 先に null にして handleEnd の自動再開を抑止
    if (rec) {
      try {
        rec.stop();
      } catch {
        // 無視
      }
    }

    consecutiveErrorsRef.current = 0;
    setInterimOriginal('');
    setErrorMessage(null);

    // 翻訳ドレインが残っているなら translating のまま、空なら即 idle
    if (queueRef.current.length === 0 && !drainingRef.current) {
      setStatus('idle');
    }
  }, [flushPending]);

  const start = useCallback(async () => {
    setErrorMessage(null);

    if (!apiKey) {
      setStatus('error');
      setErrorMessage('APIキーが設定されていません。CONFから設定してください。');
      return;
    }

    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      setStatus('error');
      setErrorMessage('お使いのブラウザは音声認識（Web Speech API）に対応していません。iPhone Safari または Android Chrome で開いてください。');
      return;
    }

    // iOS Safari の制約：getUserMedia と SR の起動は同じユーザー操作の中で
    // 直接呼ぶ必要がある。ただし getUserMedia の許可ダイアログは別タブを
    // 開かないため、await の前に Recognition を作ってしまう。
    try {
      // マイク許可（実際の音声は SR が裏で別系統に取りにいくが、
      // この呼び出しで「マイク使うよ」許可を確実に取っておく）
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // ここで取得したストリームは SR 用ではないので、即座に解放
      stream.getTracks().forEach((t) => t.stop());
    } catch (err) {
      console.error('[useInterpreter] mic permission failed:', err);
      setStatus('error');
      setErrorMessage('マイクの使用が許可されていません。ブラウザ設定からマイクを許可してください。');
      return;
    }

    const recognition = new Ctor();
    recognition.lang = RECOGNITION_LANG;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.onresult = handleResult;
    recognition.onerror = handleError;
    recognition.onend = handleEnd;

    recognitionRef.current = recognition;
    consecutiveErrorsRef.current = 0;
    queueRef.current = [];
    pendingSegmentRef.current = '';
    if (flushTimerRef.current) {
      window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    setFinalOriginal('');
    setInterimOriginal('');
    setPendingOriginal('');
    setJapanese('');
    setStatus('listening');

    try {
      recognition.start();
    } catch (err) {
      console.error('[useInterpreter] recognition.start failed:', err);
      recognitionRef.current = null;
      setStatus('error');
      setErrorMessage('音声認識を開始できませんでした。');
    }
  }, [apiKey, handleResult, handleError, handleEnd]);

  const reset = useCallback(() => {
    pendingSegmentRef.current = '';
    if (flushTimerRef.current) {
      window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    setFinalOriginal('');
    setInterimOriginal('');
    setPendingOriginal('');
    setJapanese('');
    setErrorMessage(null);
    setStatus('idle');
  }, []);

  // アンマウント時に必ず止める（バッファタイマーも掃除）
  useEffect(() => {
    return () => {
      const rec = recognitionRef.current;
      recognitionRef.current = null;
      if (rec) {
        try {
          rec.stop();
        } catch {
          // 無視
        }
      }
      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, []);

  return {
    status,
    finalOriginal,
    interimOriginal,
    pendingOriginal,
    isBuffering: pendingOriginal.length > 0,
    japanese,
    errorMessage,
    start,
    stop,
    reset,
  };
}
